import {
  build_websocket_url,
  create_request_id,
  DEFAULT_WS_PATH,
} from "./one_chat_websocket_helpers";
import { invoke_websocket_callback } from "./one_chat_websocket_events";
import {
  attach_socket_handlers,
  calculate_reconnect_delay,
  close_socket,
  create_socket,
  is_socket_connecting_or_open,
} from "./one_chat_websocket_connection";
import {
  is_response_payload,
  parse_socket_payload,
  reject_pending_requests,
  resolve_pending_response,
} from "./one_chat_websocket_message_handlers";

function build_default_options(options = {}) {
  return {
    path: DEFAULT_WS_PATH,
    token: null,
    reconnect_min_delay: 2000,
    reconnect_max_delay: 8000,
    reconnect_jitter: 500,
    heartbeat_interval: 30000,
    ...options,
  };
}

export default class OneChatWebSocketClient {
  constructor(options = {}) {
    this.options = build_default_options(options);
    this.socket = null;
    this.closed_by_client = false;
    this.reconnect_attempts = 0;
    this.reconnect_timer = null;
    this.heartbeat_timer = null;
    this.request_counter = 0;
    this.pending_requests = new Map();
  }

  connect() {
    if (is_socket_connecting_or_open(this.socket)) return;

    this.closed_by_client = false;
    this.clear_reconnect_timer();
    this.socket = create_socket(build_websocket_url(this.options));
    attach_socket_handlers(this, this.socket);
  }

  disconnect() {
    this.closed_by_client = true;
    this.clear_reconnect_timer();
    this.clear_heartbeat();
    this.reject_pending_requests(new Error("WebSocket disconnected"));
    close_socket(this.socket);
  }

  handle_open(event) {
    this.reconnect_attempts = 0;
    this.start_heartbeat();
    this.invoke_callback("on_open", event);
  }

  handle_close(event) {
    this.clear_heartbeat();
    this.invoke_callback("on_close", event);
    if (!this.closed_by_client) this.schedule_reconnect();
  }

  handle_message(event) {
    const payload = parse_socket_payload(event);
    if (is_response_payload(payload)) {
      this.handle_response(payload);
      return;
    }
    this.invoke_callback("on_event", payload);
  }

  invoke_callback(name, payload) {
    invoke_websocket_callback(this.options, name, payload);
  }

  schedule_reconnect() {
    this.reconnect_attempts += 1;
    const delay = calculate_reconnect_delay(this.options, this.reconnect_attempts);
    this.reconnect_timer = window.setTimeout(() => this.connect(), delay);
  }

  clear_reconnect_timer() {
    if (!this.reconnect_timer) return;
    window.clearTimeout(this.reconnect_timer);
    this.reconnect_timer = null;
  }

  start_heartbeat() {
    this.clear_heartbeat();
    this.heartbeat_timer = window.setInterval(
      () => this.send({ type: "presence.heartbeat" }),
      this.options.heartbeat_interval
    );
  }

  clear_heartbeat() {
    if (!this.heartbeat_timer) return;
    window.clearInterval(this.heartbeat_timer);
    this.heartbeat_timer = null;
  }

  is_open() {
    return Boolean(this.socket && this.socket.readyState === WebSocket.OPEN);
  }

  send(payload) {
    if (!this.is_open()) return false;
    this.socket.send(JSON.stringify(payload));
    return true;
  }

  request(type, payload = {}, timeout_ms = 30000) {
    if (!this.is_open()) {
      return Promise.reject(new Error("WebSocket is not connected"));
    }

    const request_id = this.next_request_id();
    return this.create_pending_request(request_id, type, payload, timeout_ms);
  }

  next_request_id() {
    this.request_counter += 1;
    return create_request_id(this.request_counter);
  }

  create_pending_request(request_id, type, payload, timeout_ms) {
    return new Promise((resolve, reject) => {
      const timeout = this.create_request_timeout(request_id, reject, type, timeout_ms);
      this.pending_requests.set(request_id, { resolve, reject, timeout });
      this.send({ type, request_id, ...payload });
    });
  }

  create_request_timeout(request_id, reject, type, timeout_ms) {
    return window.setTimeout(() => {
      this.pending_requests.delete(request_id);
      reject(new Error(`WebSocket request timed out: ${type}`));
    }, timeout_ms);
  }

  handle_response(payload) {
    resolve_pending_response(this.pending_requests, payload);
  }

  reject_pending_requests(error) {
    reject_pending_requests(this.pending_requests, error);
  }

  subscribe(conversation_id) {
    return this.send({ type: "conversation.subscribe", conversation_id });
  }

  send_message(conversation_id, content, extra = {}) {
    return this.send({
      type: "message.send",
      conversation_id,
      content: content || "",
      ...extra,
    });
  }

  mark_delivered(conversation_id, message_ids) {
    return this.send({ type: "message.delivered", conversation_id, message_ids });
  }

  mark_read(conversation_id, last_message_id) {
    return this.send({ type: "message.read", conversation_id, last_message_id });
  }

  start_typing(conversation_id) {
    return this.send({ type: "typing.start", conversation_id });
  }

  stop_typing(conversation_id) {
    return this.send({ type: "typing.stop", conversation_id });
  }

  react_message(conversation_id, message_id, reaction) {
    return this.request("message.react", { conversation_id, message_id, reaction });
  }

  edit_message(conversation_id, message_id, content) {
    return this.request("message.edit", { conversation_id, message_id, content });
  }

  delete_message(conversation_id, message_id, options = {}) {
    return this.request("message.delete", {
      conversation_id,
      message_id,
      delete_for_everyone: Boolean(options.delete_for_everyone),
      scope: options.scope || "me",
    });
  }

  pin_message(conversation_id, message_id, pin) {
    return this.request("message.pin", { conversation_id, message_id, pin });
  }

  list_pinned_messages(conversation_id) {
    return this.request("message.pinned.list", { conversation_id });
  }
}

export { build_websocket_url };
